import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { PUZZLE_CATEGORIES, JIGSAW_PUZZLES, DIFFICULTY_CONFIG } from '../../data/puzzles';

const CategoryPage: React.FC = () => {
    const history = useHistory();
    const { categoryId } = useParams<{ categoryId: string }>();

    const category = PUZZLE_CATEGORIES.find(c => c.id === categoryId);
    const puzzles = JIGSAW_PUZZLES.filter(p => p.category === categoryId);

    if (!category) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h1 style={{ color: theme.colors.textPrimary }}>Category not found</h1>
                <button onClick={() => history.push('/')} style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    backgroundColor: theme.colors.accent,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                }}>
                    Go Home
                </button>
            </div>
        );
    }

    return (
        <div style={{
            padding: '20px',
            maxWidth: '800px',
            margin: '0 auto',
            paddingBottom: '100px',
        }}>
            {/* Category Header */}
            <div style={{
                textAlign: 'center',
                marginBottom: '32px',
                padding: '24px',
                backgroundColor: category.color + '20',
                borderRadius: '20px',
            }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>{category.icon}</div>
                <h1 style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                }}>
                    {category.name}
                </h1>
                <p style={{
                    color: theme.colors.textSecondary,
                    fontSize: '14px',
                }}>
                    {category.description}
                </p>
                <p style={{
                    color: theme.colors.textSecondary,
                    fontSize: '13px',
                    marginTop: '8px',
                }}>
                    {puzzles.length} puzzles available
                </p>
            </div>

            {/* Back Button */}
            <button
                onClick={() => history.push('/')}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${theme.colors.cardBorder}`,
                    borderRadius: '8px',
                    color: theme.colors.textPrimary,
                    cursor: 'pointer',
                    marginBottom: '24px',
                }}
            >
                ← Back to Home
            </button>

            {/* Puzzle Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: '16px',
            }}>
                {puzzles.map(puzzle => {
                    const config = DIFFICULTY_CONFIG[puzzle.difficulty];
                    return (
                        <div
                            key={puzzle.id}
                            onClick={() => !puzzle.locked && history.push(`/play/${puzzle.id}`)}
                            style={{
                                borderRadius: '16px',
                                overflow: 'hidden',
                                backgroundColor: theme.colors.cardBg,
                                boxShadow: theme.shadows.md,
                                cursor: puzzle.locked ? 'not-allowed' : 'pointer',
                                opacity: puzzle.locked ? 0.6 : 1,
                                transition: 'transform 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                if (!puzzle.locked) {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = theme.shadows.lg;
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = theme.shadows.md;
                            }}
                        >
                            {/* Thumbnail */}
                            <div style={{
                                width: '100%',
                                paddingTop: '100%',
                                position: 'relative',
                                backgroundImage: `url(${puzzle.url})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}>
                                {/* Difficulty Badge */}
                                <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    padding: '4px 8px',
                                    backgroundColor: config.color,
                                    color: '#fff',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                }}>
                                    {config.pieces} pcs
                                </div>

                                {/* Lock overlay */}
                                {puzzle.locked && (
                                    <div style={{
                                        position: 'absolute',
                                        inset: 0,
                                        backgroundColor: 'rgba(0,0,0,0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '32px',
                                    }}>
                                        🔒
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <div style={{ padding: '12px' }}>
                                <div style={{
                                    fontWeight: 600,
                                    color: theme.colors.textPrimary,
                                    fontSize: '14px',
                                    marginBottom: '4px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                    {puzzle.title}
                                </div>
                                <div style={{
                                    display: 'flex',
                                    gap: '8px',
                                    fontSize: '12px',
                                    color: theme.colors.textSecondary,
                                }}>
                                    <span>⏱ {config.time}</span>
                                    <span>✨ {puzzle.xpReward} XP</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CategoryPage;
